import type { RouteRecordRaw } from 'vue-router'

/**
 * The public credential screens.
 *
 * `meta.public` is what the global guard reads to return immediately — it never asks the
 * backend whether a visitor may see the login form.
 *
 * Every component is lazy. The login screen is the first thing an unauthenticated visitor
 * loads, and it has no business pulling the shell, the data grid or the charting code with
 * it.
 */
export const authRoutes: readonly RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { public: true, layout: 'auth', title: 'Sign in' },
  },

  {
    // Public to the ROUTER, not to the API: the challenge cookie `login` set is the
    // credential, and the backend refuses the code without it. No session exists yet, so
    // the global guard must not demand one.
    path: '/two-factor',
    name: 'two-factor',
    component: () => import('@/views/auth/TwoFactorView.vue'),
    meta: { public: true, layout: 'auth', title: 'Two-factor code' },
  },

  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { public: true, layout: 'auth', title: 'Reset your password' },
  },

  {
    // The PATH is a contract: `zapio-backend`'s `passwordResetMail` builds
    // `${ADMIN_PANEL_URL}/reset-password?token=…`. Renaming this breaks every email.
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { public: true, layout: 'auth', title: 'Choose a new password' },
  },

  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/views/auth/ChangePasswordView.vue'),
    /**
     * **Not public**, and not permission-gated either.
     *
     * It needs a session — the backend requires the current password even though the caller
     * is signed in — but it is the one authenticated route the password lock must not
     * block, because it is the route that clears the lock. `sessionGuard` exempts it by
     * name for exactly that reason; the backend exempts its handler with
     * `@AllowedWhilePasswordPending()` rather than by path, and both exemptions are
     * deliberately narrow.
     */
    meta: { layout: 'auth', title: 'Choose a password' },
  },
]
